import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import type { WorkKind, WorkPriority } from "../lib/types";

// Minimal create modal wired to POST /work-items. Status is omitted so the server resolves the
// tenant's first non-terminal status for the kind (never hardcoded client-side).
export function NewItemModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<WorkKind>("task");
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<WorkPriority>("normal");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createWorkItem({ kind, title, priority }),
    onSuccess: () => {
      void qc.invalidateQueries();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create work item"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 50 }}
    >
      <div className="glass-tile" onClick={(e) => e.stopPropagation()} style={{ padding: 24, width: 440, maxWidth: "92vw" }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>New {kind}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (title.trim()) create.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            {(["task", "ticket"] as const).map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => setKind(k)}
                className={`nav ${kind === k ? "on" : ""}`}
                style={{ padding: "6px 13px", fontSize: 12, textTransform: "capitalize" }}
                aria-pressed={kind === k}
              >
                {k}
              </button>
            ))}
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--text-3)", fontWeight: 600 }}>Title</span>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "9px 12px", fontSize: 13.5, color: "var(--text-1)", fontFamily: "inherit" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--text-3)", fontWeight: 600 }}>Priority</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as WorkPriority)}
              style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "9px 12px", fontSize: 13.5, color: "var(--text-1)", fontFamily: "inherit" }}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          {error && <div style={{ fontSize: 12, color: "var(--red)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} className="nav" style={{ padding: "8px 14px", fontSize: 12.5 }}>
              Cancel
            </button>
            <button type="submit" className="btn-gold" style={{ padding: "8px 16px", cursor: "pointer" }} disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
